/**
 * Default user feedback for failed queries and mutations that no component
 * reports itself.
 *
 * The 2026-10-04 UIX root-cause sweep found two systemic gaps:
 *  - ~425 `useMutation` calls (in ~166 files) with no `onError`, no per-call
 *    `onError` and no render of `mutation.error`: a 403/409/500 on Save,
 *    Delete, Sync, Cancel or Run vanished and the button looked dead
 *    (hyp-j2uu, hyp-i5kp, hyp-rii2, hyp-fn6xm).
 *  - ~629 `useQuery` calls (in ~348 files) whose error state is never read:
 *    a failed load rendered the same "No items" empty state as a real zero
 *    (the false-empty class tracked by hyp-ji3l).
 * Fixing them screen by screen does not stop the next one, so the
 * QueryClient's MutationCache and QueryCache report what nothing else handles.
 *
 * Opt-outs, typed through the TanStack `Register` meta:
 *   meta.errorFeedback = 'inline'  the component renders the error itself
 *                        'silent'  background work whose failure must not
 *                                  interrupt the user (presence, progress)
 *   meta.errorTitle               overrides the toast title
 * A mutation with its own `onError` is always treated as handled.
 *
 * Queries only toast when there is no data to show (a first load that
 * failed — exactly the false-empty case) and the failure is unambiguous: a
 * 5xx, a network/timeout error, or a response that broke its contract.
 * 401 (the auth flow owns it), 403 (role-gated widgets) and 404 (frequently
 * "not configured yet") stay with the component.
 */
import axios from 'axios';
import type { Mutation, Query } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiContractError, getErrorMessage } from '@/platform/api-client/client';

export type ErrorFeedback = 'toast' | 'inline' | 'silent';

export interface HyperionErrorFeedbackMeta extends Record<string, unknown> {
  /** Who tells the user about a failure. Defaults to a toast from the QueryClient. */
  errorFeedback?: ErrorFeedback;
  /** Toast title for the default feedback. */
  errorTitle?: string;
}

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: HyperionErrorFeedbackMeta;
    queryMeta: HyperionErrorFeedbackMeta;
  }
}

type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;
type AnyQuery = Query<unknown, unknown, unknown, readonly unknown[]>;

function wantsDefaultFeedback(meta: HyperionErrorFeedbackMeta | undefined): boolean {
  const feedback = meta?.errorFeedback;
  return feedback === undefined || feedback === 'toast';
}

function showToast(title: string, error: unknown): void {
  const description = getErrorMessage(error);
  // Same title + message collapses into one toast while it is visible.
  toast.error(title, { id: `unhandled-error:${title}:${description}`, description });
}

/** True when the default toast should be shown for this failed mutation. */
export function needsDefaultErrorFeedback(mutation: Pick<AnyMutation, 'options'>): boolean {
  if (mutation.options.onError) return false;
  return wantsDefaultFeedback(mutation.options.meta);
}

/** MutationCache `onError` hook: surface failures nothing else reports. */
export function reportUnhandledMutationError(error: unknown, mutation: AnyMutation): void {
  if (!needsDefaultErrorFeedback(mutation)) return;
  showToast(mutation.options.meta?.errorTitle ?? 'That action failed', error);
}

/** A failure that is certainly not an empty result or a permission boundary. */
export function isUnambiguousLoadFailure(error: unknown): boolean {
  if (error instanceof ApiContractError) return true;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === undefined) return error.code !== 'ERR_CANCELED';
    return status >= 500;
  }
  return false;
}

/** True when the default toast should be shown for this failed query. */
export function needsDefaultQueryErrorFeedback(
  query: Pick<AnyQuery, 'meta' | 'state'>,
  error: unknown
): boolean {
  if (!wantsDefaultFeedback(query.meta)) return false;
  // Stale data is still on screen: a failed background refetch is not a
  // false empty, and repeating a toast on every poll would be noise.
  if (query.state.data !== undefined) return false;
  return isUnambiguousLoadFailure(error);
}

/** QueryCache `onError` hook: a first load that failed must not read as empty. */
export function reportUnhandledQueryError(error: unknown, query: AnyQuery): void {
  if (!needsDefaultQueryErrorFeedback(query, error)) return;
  showToast(query.meta?.errorTitle ?? 'Some data could not be loaded', error);
}
